import { Box, Flex, Menu, MenuButton, MenuItem, MenuList, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { CaretDown } from 'components/Icons/Icons';
import { ReactComponent as MyChoooseText } from 'assets/svgs/my-chooose.svg';
import { appContextSelector } from 'modules/app/appSlice';
import { lighten, rem, rgba } from 'polished';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { mq } from 'styles/breakpoints';
import { ReactComponent as ChoooseBlush } from 'assets/logos/chooose-blush.svg';
import { ReactComponent as ChooosePink } from 'assets/logos/chooose-pink.svg';
import { useTranslation } from 'react-i18next';
import { Button } from 'components';
import useContextSwitcher from 'hooks/useContextSwitcher';

const StyledBox = styled(Box)`
  ${mq.xl} {
    margin-right: 20px;
  }
`;

const StyledMenuItem = styled(MenuItem)<{ color?: string; selected?: boolean }>`
  height: ${(props): string => (props.color ? '64px' : '50px')};
  position: relative;
  font-size: ${rem(15)};
`;

const StyledMenuButton = styled(MenuButton)`
  font-size: ${rem(15)};
  font-weight: 500;
  height: 50px;
  border-radius: 9px;
  justify-content: flex-start;
  padding: 0 20px;

  & > span:last-of-type svg {
    font-size: ${rem(24)};
    margin-left: auto;
  }
`;

const StyledMenuList = styled(MenuList)`
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
`;

const Overlay = styled.div`
  content: '';
  cursor: pointer;
  position: absolute;
  z-index: 999;
  background: transparent;
  height: 58px;
  left: -10px;
  top: 0;
  right: -10px;
`;

const StyledMyChooose = styled(MyChoooseText)`
  display: none;
`;

const ButtonLabel = styled(Box)`
  font-size: ${rem(22)};
  font-weight: bold;
`;

interface Props {
  items: ContextMenuItem[];
  current: PortalContext;
  fullSize?: boolean;
}

const ContextSwitcher: FC<Props> = ({ items, current, fullSize = false }) => {
  const { colors } = useTheme();
  const currentContext = useSelector(appContextSelector);
  const { t } = useTranslation('common');
  const { switchContext } = useContextSwitcher();

  const preventFocus = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    if (document.activeElement) {
      (document.activeElement as HTMLDivElement).blur();
    }
  };

  const prevent = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
  };

  const chosenContext = items.find(item => item.value === current);

  const svgStyles = { width: '95px', height: '100%', marginBottom: '6px' };
  return (
    <StyledBox>
      <Menu autoSelect={false} placement="bottom-end">
        {({ isOpen }): React.ReactNode => (
          <>
            <StyledMenuButton
              rightIcon={<CaretDown />}
              onClick={prevent}
              bgColor={chosenContext?.label === 'My' ? colors.pink[200] : colors.pink[500]}
              color={chosenContext?.label === 'My' ? colors.gray[900] : colors.white}
              as={Button}
              w={fullSize ? '100%' : '240px'}
              fontFamily="body"
              sx={{
                '&:hover': {
                  bgColor: rgba(chosenContext?.label === 'My' ? colors.pink[200] : colors.pink[500], 0.9),
                },
                '& > span:last-of-type svg': {
                  color: chosenContext?.label === 'My' ? colors.gray[900] : colors.white,
                },
              }}
            >
              {/* This svg needs to render as disply none in order to properly display it in menu. This is weird bug that for some reason this svg will be cropped when weChooose svg is rendered first */}
              <Flex justify="center" align="center" w="100%">
                <StyledMyChooose />

                {chosenContext?.inverted && (
                  <>
                    {chosenContext?.label === 'My' ? (
                      <ChooosePink title={t('contextSwitcher.myChoooseButton')} data-testid="my-chooose-button" style={svgStyles} />
                    ) : (
                      <ChoooseBlush title={t('contextSwitcher.weChoooseButton')} data-testid="we-chooose-button" style={svgStyles} />
                    )}
                  </>
                )}
                <ButtonLabel as="span" fontFamily="heading" color={chosenContext?.label === 'My' ? colors.gray[900] : colors.white}>
                  {chosenContext?.label}
                </ButtonLabel>
                {!chosenContext?.inverted && (
                  <>
                    {chosenContext?.label === 'My' ? (
                      <ChooosePink title={t('contextSwitcher.myChoooseButton')} data-testid="my-chooose-button" style={svgStyles} />
                    ) : (
                      <ChoooseBlush title={t('contextSwitcher.weChoooseButton')} data-testid="we-chooose-button" style={svgStyles} />
                    )}
                  </>
                )}
              </Flex>
            </StyledMenuButton>
            <StyledMenuList w={fullSize ? 'calc(100% - 50px)' : '180px'} boxShadow={`0 2px 50px ${rgba(colors.black, 0.17)}`} bg={colors.white}>
              {items.map((item: ContextMenuItem) => {
                const selected = item.value === currentContext;
                const color = selected ? colors.pink[500] : colors.pink[200];
                const logoVersion = selected ? (
                  <ChoooseBlush title={item.title} data-testid={item.testId} style={svgStyles} />
                ) : (
                  <ChooosePink title={item.title} data-testid={item.testId} style={svgStyles} />
                );
                return (
                  <StyledMenuItem
                    id={`${item.value} chooose`}
                    selected={selected}
                    color={color}
                    bg={color ? color : selected ? colors.gray[100] : 'transparent'}
                    _hover={{
                      background: color ? lighten(0.1, color) : selected ? colors.gray[100] : 'transparent',
                    }}
                    key={item.value}
                    onClick={(): void => switchContext(item.value)}
                  >
                    {item.inverted && logoVersion}
                    <ButtonLabel as="span" fontFamily="heading" color={selected ? colors.white : colors.gray[900]}>
                      {item?.label}
                    </ButtonLabel>
                    {!item.inverted && logoVersion}
                  </StyledMenuItem>
                );
              })}
            </StyledMenuList>
            {isOpen && <Overlay onClick={preventFocus} />}
          </>
        )}
      </Menu>
    </StyledBox>
  );
};

export default ContextSwitcher;
